import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import type { Item, Owner } from '../types';
import { useWheelbarrow } from '../context/WheelbarrowContext';
import { useData } from '../context/DataContext';
import { formatGBP } from '../utils/currency';
import StarRating from './StarRating';
import ImageGallery from './ImageGallery';
import Map from './Map';
import './ItemDetail.css';

interface ItemDetailProps {
  item: Item;
  onClose: () => void;
}

const convertToImageObjects = (urls: string[]) => {
  return urls.map((url, index) => ({
    id: `img-${index}`,
    url,
    alt: 'Item image'
  }));
};

const ItemDetail: React.FC<ItemDetailProps> = ({ item, onClose }) => {
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [highlightedDates, setHighlightedDates] = useState<Date[]>([]);
  const [owner, setOwner] = useState<Owner | null>(null);
  const [ownerLoading, setOwnerLoading] = useState(true);
  const { addItem } = useWheelbarrow();
  const { getOwner } = useData();

  // Fetch owner information
  useEffect(() => {
    const fetchOwner = async () => {
      try {
        setOwnerLoading(true);
        const ownerData = await getOwner(item.ownerId);
        setOwner(ownerData);
      } catch (error) {
        console.error('Error fetching owner:', error);
        setOwner(null);
      } finally {
        setOwnerLoading(false);
      }
    };

    fetchOwner();
  }, [item.ownerId, getOwner]);

  const getDaysBetweenDates = (start: Date, end: Date): number => {
    const diffTime = Math.abs(end.getTime() - start.getTime());
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1; // Add 1 to include both start and end dates
  };

  const isItemAvailable = (start: Date, end: Date): boolean => {
    if (!item.rentalPeriods || item.rentalPeriods.length === 0) return true;
    
    return !item.rentalPeriods.some(period => {
      const periodStart = new Date(period.startDate);
      const periodEnd = new Date(period.endDate);
      
      // Check if there's any overlap between the periods
      return (
        (start <= periodEnd && start >= periodStart) ||
        (end <= periodEnd && end >= periodStart) ||
        (start <= periodStart && end >= periodEnd)
      );
    });
  };

  // Update highlighted date range when start date changes
  useEffect(() => {
    if (startDate) {
      const dates: Date[] = [];
      // Add minimum required days
      for (let i = 0; i < item.minDays; i++) {
        const date = new Date(startDate);
        date.setDate(startDate.getDate() + i);
        dates.push(date);
      }
      // Add maximum allowed days
      if (item.maxDays) {
        for (let i = item.minDays; i < item.maxDays; i++) {
          const date = new Date(startDate);
          date.setDate(startDate.getDate() + i);
          dates.push(date);
        }
      }
      setHighlightedDates(dates);
    } else {
      setHighlightedDates([]);
    }
  }, [startDate, item.minDays, item.maxDays]);

  const handleDateChange = (dates: [Date | null, Date | null]) => {
    const [start, end] = dates;
    
    if (start && end) {
      const days = getDaysBetweenDates(start, end);
      
      // If selected range is less than minimum days, extend end date
      if (days < item.minDays) {
        const adjustedEnd = new Date(start);
        adjustedEnd.setDate(start.getDate() + item.minDays - 1);
        setStartDate(start);
        setEndDate(adjustedEnd);
        return;
      }
      
      // If selected range exceeds maximum days, adjust end date
      if (item.maxDays && days > item.maxDays) {
        const adjustedEnd = new Date(start);
        adjustedEnd.setDate(start.getDate() + item.maxDays - 1);
        setStartDate(start);
        setEndDate(adjustedEnd);
        return;
      }
    }
    
    setStartDate(start);
    setEndDate(end);
  };

  const handleAddToWheelbarrow = () => {
    if (startDate && endDate) {
      const days = getDaysBetweenDates(startDate, endDate);
      addItem(item, days);
      onClose();
    }
  };

  const minDate = new Date();

  const getSelectedDays = (): number | null => {
    if (startDate && endDate) {
      return getDaysBetweenDates(startDate, endDate);
    }
    return null;
  };

  const selectedDays = getSelectedDays();
  const isDisabled = Boolean(
    !startDate || 
    !endDate || 
    (startDate && endDate && !isItemAvailable(startDate, endDate)) ||
    (selectedDays !== null && selectedDays < item.minDays) ||
    (selectedDays !== null && item.maxDays && selectedDays > item.maxDays)
  );

  // Custom day class names for styling
  const getDayClassName = (date: Date) => {
    if (!startDate) return "";
    
    const dayCount = getDaysBetweenDates(startDate, date);
    
    if (dayCount <= item.minDays) {
      return "minimum-days";
    }
    if (item.maxDays && dayCount <= item.maxDays) {
      return "maximum-days";
    }
    return "";
  };

  // Generate array of dates that are already booked
  const bookedDates = useMemo(() => {
    if (!item.rentalPeriods || item.rentalPeriods.length === 0) return [];

    const dates: Date[] = [];
    item.rentalPeriods.forEach(period => {
      const start = new Date(period.startDate);
      const end = new Date(period.endDate);
      
      // Add each date in the period to the array
      const current = new Date(start);
      while (current <= end) {
        dates.push(new Date(current));
        current.setDate(current.getDate() + 1);
      }
    });
    return dates;
  }, [item.rentalPeriods]);

  // Check if a date should be disabled
  const isDateDisabled = (date: Date): boolean => {
    return bookedDates.some(bookedDate => 
      bookedDate.getFullYear() === date.getFullYear() &&
      bookedDate.getMonth() === date.getMonth() &&
      bookedDate.getDate() === date.getDate()
    );
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={e => e.stopPropagation()}>
        <button className="close-button" onClick={onClose}>&times;</button>
        
        <div className="item-detail-content">
          <h2>{item.name}</h2>
          
          <div className="item-info">
            {/* Description Section - First */}
            <div className="item-description">
              <p className="description">{item.description}</p>
            </div>

            {/* Image Gallery Section - Second */}
            <div className="item-gallery-section">
              <div className="item-gallery">
                <ImageGallery images={convertToImageObjects(item.images)} />
              </div>
            </div>

            {/* Rental Details with Calendar - Third */}
            <div className="item-details">
              <div className="rental-details">
                <div className="price-info">
                  <span className="daily-rate">£{item.dailyRateGBP.toFixed(2)}</span>
                  <span className="per-day">per day</span>
                </div>
                
                <div className="date-selector">
                  <label>Select Rental Period:</label>
                  <DatePicker
                    selected={startDate}
                    onChange={handleDateChange}
                    startDate={startDate}
                    endDate={endDate}
                    selectsRange
                    inline
                    minDate={minDate}
                    monthsShown={1}
                    calendarClassName="rental-calendar"
                    dayClassName={getDayClassName}
                    highlightDates={highlightedDates}
                    placeholderText="Select start date"
                    excludeDates={bookedDates}
                    filterDate={date => !isDateDisabled(date)}
                  />
                  <div className="rental-period-info">
                    {startDate && endDate ? (
                      <>
                        <p>Selected period: {selectedDays} days</p>
                        <p className="total-cost">
                          Total Cost: £{(item.dailyRateGBP * selectedDays!).toFixed(2)}
                        </p>
                      </>
                    ) : startDate ? (
                      <p>
                        Select end date (min: {item.minDays} days
                        {item.maxDays ? `, max: ${item.maxDays} days` : ''})
                      </p>
                    ) : (
                      <p>Please select start date to begin</p>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Location and Map Section - Fourth */}
            <div className="location-info">
              <h3>Location</h3>
              {item.location ? (
                <>
                  <p>
                    <strong>{item.location.address || 'Address not available'}</strong><br />
                    {item.location.city || 'City not available'}<br />
                    {item.location.postcode || 'Postcode not available'}
                  </p>
                  <Map location={item.location} />
                </>
              ) : (
                <p>Location not available</p>
              )}
            </div>

            {/* Owner Info Section - Fifth */}
            <div className="owner-info">
              <div className="owner-header">
                {ownerLoading ? (
                  <p className="owner-name">Owner: Loading...</p>
                ) : owner ? (
                  <Link to={`/owner/${owner.id}`} className="owner-link">
                    <p className="owner-name">Owner: {owner.name}</p>
                  </Link>
                ) : (
                  <p className="owner-name">Owner: Unknown Owner</p>
                )}
                <StarRating 
                  rating={owner?.rating || 0}
                  totalRatings={owner?.totalRatings || 0}
                />
              </div>
              <p>Minimum rental period: {item.minDays} day{item.minDays > 1 ? 's' : ''}</p>
              {item.maxDays && (
                <p>Maximum rental period: {item.maxDays} days</p>
              )}
            </div>
          </div>

          {/* Add to Wheelbarrow Button - Last */}
          <button
            className="add-to-wheelbarrow"
            onClick={handleAddToWheelbarrow}
            disabled={isDisabled}
          >
            {startDate && endDate && !isItemAvailable(startDate, endDate) ? 'Unavailable for Selected Dates' :
             !startDate || !endDate ? 'Select Dates to Continue' :
             selectedDays !== null && selectedDays < item.minDays ? `Minimum ${item.minDays} days required` :
             selectedDays !== null && item.maxDays && selectedDays > item.maxDays ? `Maximum ${item.maxDays} days allowed` :
             'Add to Wheelbarrow'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ItemDetail; 